import { Icon } from '../components/Icon'
import { StatusBadge } from '../components/badges'
import { PrevisionBadge } from '../components/PrevisionBadge'
import { actionsFor, resolveEligibility, isInProgress, isPending, elapsedUrgency, isOverdueButToday } from '../domain/engine'
import { useTick } from '../components/TickContext'
import type { Ticket, TicketAction } from '../domain/types'
import type { AppActions, TicketActionKind } from '../store/types'
import { env } from '../config/env'

export interface ServiceCardProps {
  ticket: Ticket
  tickets: Ticket[]
  blockStyle: string
  actions?: AppActions
  readOnly?: boolean
}

export function ServiceCard({ ticket, tickets, blockStyle, actions, readOnly = false }: ServiceCardProps) {
  const info = resolveEligibility(tickets, ticket)
  const st = ticket.status
  const overdue = ticket.isOverdue || ticket.scheduleState === 'Expired'
  // singleDay: some só o rótulo "Atrasado", independente de quando venceu.
  const overdueLabel = !env.singleDay && overdue
  // Vencer no mesmo dia não é mais estado de bloqueio: o ticket segue chamável, sem o aviso
  // "Sessão vencida" e sem supressão das ações. Vencida há mais de 1 dia — ou sem data de
  // sessão que prove o contrário — continua bloqueando, com aviso e Reagendar.
  const overdueBlocking = overdue && !isOverdueButToday(ticket)
  const done = st === 'Completed' || st === 'Canceled'
  const inProg = isInProgress(st)
  const pending = isPending(st)
  const next = pending && info.podeIniciar
  const blocked = pending && !info.podeIniciar && !overdueBlocking
  const rescheduling = actions?.isTicketActionPending?.(ticket.ticketId, 'reschedule') ?? false
  const acts = actions ? actionsFor(tickets, ticket) : new Set<TicketAction>()
  const canReschedule = !!actions && !readOnly && (ticket.allowedActions?.includes('reschedule') || acts.has('reschedule'))
  const pendingOf = (action: TicketActionKind) => actions?.isTicketActionPending?.(ticket.ticketId, action) ?? false

  // Re-render a cada 15s para atualizar o cronômetro de minutos decorridos.
  // Antes: cada ServiceCard "Started" mantinha seu próprio setInterval(15s).
  // 7 tickets = 7 timers paralelos. Agora: 1 timer global via TickProvider.
  useTick()

  const cls = [
    'fz-svc', 'fz-block--' + blockStyle,
    done ? 'is-done' : '', inProg ? 'is-active' : '', next ? 'is-next' : '', blocked ? 'is-blocked' : '',
  ].join(' ')

  const blockingDeps = (info.bloqueadoPor || [])
    .map((id) => { const t = tickets.find((x) => x.ticketId === id); return t ? { code: t.codigoTicket, nome: t.nomeServico } : null })
    .filter(Boolean) as { code: string; nome: string }[]

  return (
    <article className={cls} data-screen-label={`Serviço ${ticket.codigoTicket}`}>
      <div className="fz-svc__top">
        <span className="fz-svc__code">{ticket.codigoTicket}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <StatusBadge status={st} blocked={blocked} overdue={overdueLabel} sm />
          {actions && (
            <button
              className="fz-icontiny"
              title="Detalhes do bilhete"
              aria-label={`Detalhes do bilhete ${ticket.codigoTicket}`}
              onClick={() => actions.openDetails(ticket.ticketId)}
            >
              <Icon name="info" size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="fz-svc__name">{ticket.nomeServico}</div>

      <div className="fz-svc__meta">
        <span><Icon name="pin" size={13} /> {ticket.sala}</span>
        {ticket.resourceNome && <span><Icon name="user" size={13} /> {ticket.resourceNome}</span>}
        {ticket.sessionDateTime && <span><Icon name="calendar" size={13} /> {ticket.sessionDateTime}</span>}
        {inProg && st === 'Started' && (() => {
          const urg = elapsedUrgency(ticket._startedAt, ticket.estMin)
          const timerCls = urg === 'danger' ? 'fz-serving__timer fz-serving__timer--danger'
            : urg === 'warning' ? 'fz-serving__timer fz-serving__timer--warning'
            : ''
          return (
            <span className={timerCls} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 'bold' }}>
              <Icon name="clock" size={13} />
              {actions ? actions.elapsedOf(ticket) : 'Em curso'}
            </span>
          )
        })()}
        {done && st === 'Completed' && <span><Icon name="check" size={13} /> {ticket.concluidoEm}</span>}
        {pending && <span><Icon name="clock" size={13} /> ~{ticket.estMin} min</span>}
        {pending && <PrevisionBadge ticket={ticket} />}
        {!!ticket.timesCalled && ticket.timesCalled > 0 && (
          <span title={`Última chamada: ${ticket.lastTimeCalled ?? '—'}`}>
            <Icon name="megaphone" size={13} /> {ticket.timesCalled}× {ticket.lastTimeCalled ? `(${ticket.lastTimeCalled})` : ''}
          </span>
        )}
      </div>

      {!readOnly && actions && !overdueBlocking && acts.size > 0 && (
        <div className="fz-svc__actions">
          {acts.has('call') && <button className="fz-btn fz-btn--primary fz-btn--sm" disabled={pendingOf('call')} aria-busy={pendingOf('call')} onClick={() => actions.call(ticket.ticketId, false, ticket.resourceId)}>{pendingOf('call') ? <span className="fz-spin" /> : <Icon name="megaphone" size={14} />} {pendingOf('call') ? 'Chamando…' : 'Chamar'}</button>}
          {acts.has('start') && <button className="fz-btn fz-btn--primary fz-btn--sm" disabled={pendingOf('start')} aria-busy={pendingOf('start')} onClick={() => actions.start(ticket.ticketId, ticket.resourceId)}>{pendingOf('start') ? <span className="fz-spin" /> : <Icon name="play" size={14} />} {pendingOf('start') ? 'Iniciando…' : 'Iniciar'}</button>}
          {acts.has('recall') && <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={pendingOf('recall')} aria-busy={pendingOf('recall')} onClick={() => actions.call(ticket.ticketId, true, ticket.resourceId)}>{pendingOf('recall') ? <><span className="fz-spin" /> Rechamando…</> : 'Rechamar'}</button>}
          {acts.has('complete') && <button className="fz-btn fz-btn--primary fz-btn--sm" disabled={pendingOf('complete')} aria-busy={pendingOf('complete')} onClick={() => actions.complete(ticket.ticketId)}>{pendingOf('complete') ? <span className="fz-spin" /> : <Icon name="check" size={14} />} {pendingOf('complete') ? 'Concluindo…' : 'Concluir'}</button>}
          {acts.has('confirm') && <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={pendingOf('confirm')} aria-busy={pendingOf('confirm')} onClick={() => actions.confirm(ticket.ticketId)}>{pendingOf('confirm') ? <><span className="fz-spin" /> Confirmando…</> : 'Confirmar'}</button>}
          {acts.has('check') && <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={pendingOf('check')} aria-busy={pendingOf('check')} onClick={() => actions.check(ticket.ticketId)}>{pendingOf('check') ? <><span className="fz-spin" /> Fazendo check-in…</> : 'Check-in'}</button>}
          {acts.has('reopen') && <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={pendingOf('reopen')} aria-busy={pendingOf('reopen')} onClick={() => actions.reopen(ticket.ticketId)}>{pendingOf('reopen') ? <><span className="fz-spin" /> Reinserindo…</> : <><Icon name="rotate" size={14} /> Reinserir</>}</button>}
          {acts.has('transfer') && <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={pendingOf('transfer')} aria-busy={pendingOf('transfer')} onClick={() => actions.openTransfer(ticket.ticketId)}>{pendingOf('transfer') ? <><span className="fz-spin" /> Transferindo…</> : <><Icon name="arrowLeftRight" size={13} /> Transferir</>}</button>}
        </div>
      )}

      <div className="fz-svc__foot">
        {overdueBlocking && (
          <div className="fz-tip fz-tip--overdue" tabIndex={0} role="note" aria-label="Sessão vencida">
            <span className="fz-lock" style={{ color: 'var(--color-danger)' }}>
              <Icon name="alert" size={13} />
              {ticket.motivoBloqueio ?? 'Sessão vencida. Reagende para continuar.'}
            </span>
            {canReschedule && (
              <button
                className="fz-btn fz-btn--ghost fz-btn--sm"
                disabled={rescheduling}
                aria-busy={rescheduling}
                onClick={() => actions.openReschedule(ticket.ticketId)}
              >
                {rescheduling ? <><span className="fz-spin" /> Reagendando...</> : 'Reagendar'}
              </button>
            )}
          </div>
        )}
        {blocked && (
          <div className="fz-tip" tabIndex={0} role="note" aria-label="Por que está bloqueado">
            <span className="fz-lock">
              <Icon name="lock" size={13} />
              {blockingDeps.length > 0
                ? `Aguardando: ${blockingDeps[0].nome}`
                : info.razao === 'concorrente' ? 'Aguardando concorrente' : 'Aguardando etapa anterior'}
            </span>
            <div className="fz-tip__pop">
              <strong>Por que está bloqueado?</strong>
              {info.motivo}
              {blockingDeps.length > 0 && <div style={{ marginTop: 5 }}>Depende de: {blockingDeps.map((d, i) => <span key={i}><code>{d.code}</code> · {d.nome}{i < blockingDeps.length - 1 ? '; ' : ''}</span>)}</div>}
            </div>
          </div>
        )}
        {done && st === 'Canceled' && <span className="fz-lock"><Icon name="x" size={13} /> Cancelado · não conta como pendente</span>}
      </div>
    </article>
  )
}
